/**
 * Buchungsstrecke C1 — die Zwei-Schritt-Zusage (Mockup Station 5, docs/KONZEPT-VERKAUFSPSYCHOLOGIE.md
 * § 4 Punkt 5): erst das Ja zur Variante, dann nur die Formalitäten, die noch fehlen.
 *
 * NUR auf der neuen Angebotsseite (Schalter `angebotsseite.v2`, sql/591). Ohne Schalter zeichnet
 * `pages/offer-public.tsx` den Annahmebereich wie bisher.
 *
 * Diese Datei rechnet nichts und schickt nichts ab. Annahme, Anzahlung, Beträge und Pflichtfelder
 * bleiben, wo sie sind (`accept()` in der Seite, POST …/respond, Server-Zahlungsvorschau): die
 * Beträge kommen fertig formatiert herein, die Eingabefelder als fertige Elemente aus der Seite
 * (dieselben Felder, dieselben Pflichtregeln wie im bisherigen Annahmebereich).
 *
 * Gast-Ansprache „Sie“ (Design-Regel öffentliche Seite). Deutsche Zitate mit „…“.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button, Card, Icon } from "./components";

type Sprache = "de" | "en";

export const ZUSAGE_T = {
  de: {
    schrittVon: (n: number) => `Schritt ${n} von 2`,
    schritt1Titel: "Ihre Auswahl",
    schritt1Text: "Passt das so? Dann fehlen nur noch wenige Angaben.",
    keineAuswahl: "Bitte wählen Sie oben zuerst eine Variante.",
    fuerGaeste: (n: number) => `für ${n} Gäste`,
    inklZubuchungen: "inkl. gewählter Zubuchungen",
    inklMwst: "inkl. MwSt.",
    aendern: "ändern",
    aendernAria: (was: string) => `${was} ändern`,
    auswahlAria: "Auswahl",
    weiter: "Ja, weiter",
    zurueck: "Zurück",
    zurueckAria: "Zurück zur Auswahl",
    schritt2Titel: "Fast geschafft",
    schritt2Fehlt: "Nur noch die fehlenden Angaben. Den Rest kennen wir schon.",
    schritt2Komplett: "Alles da. Bitte prüfen Sie kurz Ihre Angaben.",
    kontaktTitel: "Ihre Angaben",
    rechnungsadresse: "Rechnungsadresse",
    telefon: "Telefon",
    handy: "Handynummer",
    referenzZeigen: "Referenz für Ihre Buchhaltung angeben (optional)",
    kuFrage: "Firma übernimmt die Kosten?",
    kuLink: "Kostenübernahme unterschreiben lassen",
  },
  en: {
    schrittVon: (n: number) => `Step ${n} of 2`,
    schritt1Titel: "Your selection",
    schritt1Text: "Does this look right? Then only a few details are missing.",
    keineAuswahl: "Please choose an option above first.",
    fuerGaeste: (n: number) => `for ${n} guests`,
    inklZubuchungen: "incl. selected add-ons",
    inklMwst: "incl. VAT",
    aendern: "change",
    aendernAria: (was: string) => `Change ${was.toLowerCase()}`,
    auswahlAria: "Selection",
    weiter: "Yes, continue",
    zurueck: "Back",
    zurueckAria: "Back to selection",
    schritt2Titel: "Almost done",
    schritt2Fehlt: "Only the missing details. We already know the rest.",
    schritt2Komplett: "All set. Please take a quick look at your details.",
    kontaktTitel: "Your details",
    rechnungsadresse: "Billing address",
    telefon: "Phone",
    handy: "Mobile number",
    referenzZeigen: "Add a reference for your accounting (optional)",
    kuFrage: "Is a company covering the costs?",
    kuLink: "Have a cost acceptance signed",
  },
} as const;

/** Was Schritt 1 und der Kopf von Schritt 2 über die gewählte Variante zeigen — alles fertig vom Aufrufer. */
export type ZusageAuswahl = {
  titel: string;
  gaeste: number;
  /** Datum der Feier, fertig formatiert (oder null, wenn keins feststeht). */
  datum: string | null;
  /** Gesamtbetrag inkl. gewählter Zubuchungen, fertig formatiert — dieselbe Zahl wie die S0-Zusammenfassung. */
  gesamt: string;
  mitZubuchungen: boolean;
  /** Migration 731: „À la carte“ - `gesamt` ist dann das Wort „Nach Karte“, kein Betrag (kein „inkl. MwSt.“ dahinter). */
  ohneBetrag?: boolean;
  /** Gewählte Zubuchungen als Anzeigezeilen („2 × Champagner“). */
  zubuchungen: string[];
};

/** Setzt den Fokus auf die Überschrift, sobald ein Schritt nach einem Klick erscheint (nicht beim Laden). */
function useFokusBeiWechsel(aktiv: boolean) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (aktiv) ref.current?.focus();
  }, [aktiv]);
  return ref;
}

function Schrittanzeige({ n, lang }: { n: 1 | 2; lang: Sprache }) {
  const t = ZUSAGE_T[lang];
  return (
    <p className="flex items-center gap-2 whitespace-nowrap font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
      <span aria-hidden className="flex gap-1">
        <span className="h-1.5 w-6 rounded-full bg-on-surface-variant dark:bg-secondary-fixed-dim" />
        <span className={`h-1.5 w-6 rounded-full ${n === 2 ? "bg-on-surface-variant dark:bg-secondary-fixed-dim" : "bg-outline-variant dark:bg-outline/40"}`} />
      </span>
      {t.schrittVon(n)}
    </p>
  );
}

/** Schritt 1: die gewählte Variante bestätigen. Kein Formularfeld, nur das Ja. */
export function ZusageSchritt1({
  auswahl,
  lang,
  fokus,
  onWeiter,
  onAuswahlAendern,
  nebenweg,
}: {
  auswahl: ZusageAuswahl | null;
  lang: Sprache;
  /** true, wenn der Gast gerade aus Schritt 2 zurückkommt — dann bekommt die Überschrift den Fokus. */
  fokus: boolean;
  onWeiter: () => void;
  onAuswahlAendern: () => void;
  nebenweg?: ReactNode;
}) {
  const t = ZUSAGE_T[lang];
  const kopf = useFokusBeiWechsel(fokus);
  return (
    <section className="maestro-druck-aus" aria-labelledby="zusage-schritt-1-titel" data-testid="zusage-schritt-1">
      <Schrittanzeige n={1} lang={lang} />
      <h3
        id="zusage-schritt-1-titel"
        ref={kopf}
        tabIndex={-1}
        className="mt-2 font-headline-sm text-headline-sm text-on-surface outline-none dark:text-inverse-on-surface"
      >
        {t.schritt1Titel}
      </h3>
      {auswahl ? (
        <>
          <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.schritt1Text}</p>
          <AuswahlKarte auswahl={auswahl} lang={lang} onAendern={onAuswahlAendern} gross />
          <Button type="button" className="mt-6 w-full !py-4" onClick={onWeiter} data-testid="zusage-weiter">
            {t.weiter}
            <span aria-hidden className="inline-flex">
              <Icon name="arrow_forward" className="text-[18px]" />
            </span>
          </Button>
        </>
      ) : (
        <p className="mt-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim" data-testid="zusage-keine-auswahl">
          {t.keineAuswahl}
        </p>
      )}
      {nebenweg}
    </section>
  );
}

function AuswahlKarte({ auswahl, lang, onAendern, gross = false }: { auswahl: ZusageAuswahl; lang: Sprache; onAendern: () => void; gross?: boolean }) {
  const t = ZUSAGE_T[lang];
  const zeile = [auswahl.gaeste > 0 ? t.fuerGaeste(auswahl.gaeste) : null, auswahl.datum].filter(Boolean).join(" · ");
  return (
    <Card
      className={`mt-4 flex items-start gap-3 border-outline-variant dark:border-outline/40 ${gross ? "p-4" : "p-3"}`}
      data-testid="zusage-auswahl"
      aria-label={t.auswahlAria}
    >
      <span aria-hidden className="inline-flex shrink-0">
        <Icon name="check_circle" className={`text-tertiary dark:text-tertiary-fixed-dim ${gross ? "text-[28px]" : "text-[22px]"}`} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-serif text-body-md font-semibold leading-snug text-on-surface dark:text-inverse-on-surface">{auswahl.titel}</p>
        {zeile && <p className="mt-0.5 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{zeile}</p>}
        {auswahl.zubuchungen.length > 0 && (
          <ul className="mt-1 space-y-0.5 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="zusage-zubuchungen">
            {auswahl.zubuchungen.map((z) => (
              <li key={z}>+ {z}</li>
            ))}
          </ul>
        )}
        <p className="mt-1 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
          <b data-testid="zusage-gesamt">{auswahl.gesamt}</b>
          {auswahl.ohneBetrag ? "" : ` ${t.inklMwst}`}
          {auswahl.mitZubuchungen ? ` · ${t.inklZubuchungen}` : ""}
        </p>
      </div>
      <button
        type="button"
        className="shrink-0 font-body-sm text-body-sm font-semibold text-on-surface underline underline-offset-2 dark:text-inverse-on-surface"
        onClick={onAendern}
        aria-label={t.aendernAria(t.auswahlAria)}
      >
        {t.aendern}
      </button>
    </Card>
  );
}

/** Kopf von Schritt 2: Zurück, die bestätigte Auswahl kompakt, Überschrift „Fast geschafft“. */
export function ZusageSchritt2Kopf({
  auswahl,
  lang,
  fokus,
  allesDa,
  onZurueck,
}: {
  auswahl: ZusageAuswahl | null;
  lang: Sprache;
  fokus: boolean;
  /** true, wenn beim Betreten von Schritt 2 keine Pflichtangabe fehlte. */
  allesDa: boolean;
  onZurueck: () => void;
}) {
  const t = ZUSAGE_T[lang];
  const kopf = useFokusBeiWechsel(fokus);
  return (
    <div className="maestro-druck-aus" data-testid="zusage-schritt-2">
      <div className="flex items-center justify-between gap-3">
        <Schrittanzeige n={2} lang={lang} />
        <Button type="button" variant="ghost" className="shrink-0 whitespace-nowrap !px-2 !py-1" onClick={onZurueck} aria-label={t.zurueckAria} data-testid="zusage-zurueck">
          <span aria-hidden className="inline-flex">
            <Icon name="arrow_back" className="text-[18px]" />
          </span>
          {t.zurueck}
        </Button>
      </div>
      {auswahl && <AuswahlKarte auswahl={auswahl} lang={lang} onAendern={onZurueck} />}
      <h3
        ref={kopf}
        tabIndex={-1}
        className="mt-6 font-headline-sm text-headline-sm text-on-surface outline-none dark:text-inverse-on-surface"
      >
        {t.schritt2Titel}
      </h3>
      <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
        {allesDa ? t.schritt2Komplett : t.schritt2Fehlt}
      </p>
    </div>
  );
}

/** Zeile einer schon vollständigen Angabe: Wert + „ändern“ statt Formular. */
function Vorhanden({ titel, werte, lang, onAendern, testId }: { titel: string; werte: string[]; lang: Sprache; onAendern: () => void; testId: string }) {
  const t = ZUSAGE_T[lang];
  return (
    <div className="flex items-start justify-between gap-3 py-3" data-testid={testId}>
      <div className="min-w-0">
        <p className="font-label-md text-label-md text-on-surface-variant dark:text-secondary-fixed-dim">{titel}</p>
        {werte.map((w) => (
          <p key={w} className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
            {w}
          </p>
        ))}
      </div>
      <button
        type="button"
        className="shrink-0 font-body-sm text-body-sm font-semibold text-on-surface underline underline-offset-2 dark:text-inverse-on-surface"
        onClick={onAendern}
        aria-label={t.aendernAria(titel)}
      >
        {t.aendern}
      </button>
    </div>
  );
}

/**
 * Schritt 2, Angaben: was vollständig vorliegt, steht als Zusammenfassung mit „ändern“; was fehlt
 * (oder geändert werden soll), erscheint als Formular. Die Formulare kommen fertig aus der Seite —
 * dieselben Felder und Pflichtregeln wie im bisherigen Annahmebereich.
 */
export function ZusageAngaben({
  lang,
  offen,
  onOeffnen,
  adresseZeilen,
  telefon,
  handy,
  adresseFormular,
  telefonFormular,
  referenzFormular,
  referenzGesetzt,
  pflichtHinweis,
}: {
  lang: Sprache;
  offen: { adresse: boolean; telefon: boolean };
  onOeffnen: (feld: "adresse" | "telefon") => void;
  adresseZeilen: string[];
  telefon: string;
  handy: boolean;
  adresseFormular: ReactNode;
  telefonFormular: ReactNode;
  referenzFormular: ReactNode;
  referenzGesetzt: boolean;
  pflichtHinweis: ReactNode;
}) {
  const t = ZUSAGE_T[lang];
  const [referenzOffen, setReferenzOffen] = useState(referenzGesetzt);
  const zeigeReferenz = referenzOffen || referenzGesetzt;
  return (
    <div
      className="maestro-druck-aus mt-6 rounded-xl border border-outline-variant/40 bg-surface p-5 dark:border-outline/30 dark:bg-inverse-surface"
      data-testid="zusage-angaben"
    >
      <h4 className="font-label-md text-label-md font-semibold text-on-surface dark:text-inverse-on-surface">{t.kontaktTitel}</h4>
      <div className="divide-y divide-outline-variant/40 dark:divide-outline/30">
        {offen.adresse ? (
          <div className="py-3" data-testid="zusage-adresse-formular">
            <p className="mb-2 font-label-md text-label-md text-on-surface-variant dark:text-secondary-fixed-dim">{t.rechnungsadresse}</p>
            {adresseFormular}
          </div>
        ) : (
          <Vorhanden titel={t.rechnungsadresse} werte={adresseZeilen} lang={lang} onAendern={() => onOeffnen("adresse")} testId="zusage-adresse-vorhanden" />
        )}
        {offen.telefon ? (
          <div className="py-3" data-testid="zusage-telefon-formular">
            {telefonFormular}
          </div>
        ) : (
          <Vorhanden titel={handy ? t.handy : t.telefon} werte={[telefon]} lang={lang} onAendern={() => onOeffnen("telefon")} testId="zusage-telefon-vorhanden" />
        )}
        <div className="py-3">
          {zeigeReferenz ? (
            referenzFormular
          ) : (
            <button
              type="button"
              className="font-body-sm text-body-sm text-secondary underline underline-offset-2 hover:text-on-surface dark:text-secondary-fixed-dim"
              onClick={() => setReferenzOffen(true)}
            >
              {t.referenzZeigen}
            </button>
          )}
        </div>
      </div>
      {(offen.adresse || offen.telefon) && pflichtHinweis}
    </div>
  );
}

/** Kostenübernahme als Nebenweg: ein dezenter Link statt einer gleichrangigen Alternative. */
export function KostenuebernahmeNebenweg({ lang, onOeffnen }: { lang: Sprache; onOeffnen: () => void }) {
  const t = ZUSAGE_T[lang];
  return (
    <p className="maestro-druck-aus mt-6 text-center font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim" data-testid="zusage-ku-nebenweg">
      {t.kuFrage}{" "}
      <button
        type="button"
        className="font-semibold text-on-surface underline underline-offset-2 hover:no-underline dark:text-inverse-on-surface"
        onClick={onOeffnen}
      >
        {t.kuLink}
      </button>
    </p>
  );
}
